import { apiUrl } from './config'
import { ApiError } from './api'

// Customer self-service / profile: a public flow gated by a self-service token (NOT the
// operator OIDC bearer token). The token is a short bootstrap token (AccessKey onboarding)
// or a ~90-day trusted-device token (issued on a successful code verification). So these
// calls use a plain fetch (no Authorization header).

export interface OnboardResponse {
  token: string
  firstName: string
  mobilePhone: string | null
  email: string | null
  /** When the customer may next change their mobile number (ISO), or null if changeable now. */
  mobilePhoneChangeAllowedUtc: string | null
  mobilePhoneVerified: boolean
  emailVerified: boolean
}

export interface VerifyResponse {
  ok: boolean
  mobilePhoneChangeAllowedUtc: string | null
  /** Refreshed trusted-device token to persist after a successful verification. */
  token: string
}

export type Channel = 'sms' | 'email'

async function post<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(apiUrl(`/api/v1/self-service/${path}`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  })
  if (!response.ok) throw new ApiError(response.status, await response.text().catch(() => ''))
  if (response.status === 204 || response.headers.get('content-length') === '0') return undefined as T
  return (await response.json()) as T
}

export const selfServiceApi = {
  /** Bootstrap with an AccessKey. Throws ApiError 409 once a method is verified (use code login). */
  onboard: (accessKey: string) => post<OnboardResponse>('onboard', { accessKey }),
  /** Resume the profile with a stored trusted-device token. Throws ApiError 401 when expired. */
  resume: (token: string) => post<OnboardResponse>('resume', { token }),
  loginRequestCode: (identifier: string) => post<void>('login/request-code', { identifier }),
  loginVerify: (identifier: string, code: string) =>
    post<OnboardResponse>('login/verify', { identifier, code }),
  requestCode: (token: string, channel: Channel, value: string) =>
    post<void>('request-code', { token, channel, value }),
  verify: (token: string, channel: Channel, value: string, code: string) =>
    post<VerifyResponse>('verify', { token, channel, value, code }),
}

// Trusted-device token persistence (survives reloads for ~90 days until the server expires it).
const TOKEN_KEY = 'hemagg.selfservice.deviceToken'
export const deviceToken = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t: string) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
}
